import { useRef, useState } from "react";
import { Download, Upload, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Toolbar rendered on the /admin/members listing that lets the admin
 *  - download an Excel template with the exact columns to fill,
 *  - upload a filled-out .xlsx file for bulk create / update of members.
 *
 * On upload, the endpoint upserts by slug (or email) so re-uploading the
 * same file never creates duplicates. Only the row deltas modify the DB.
 */
export default function MembersImportBar({ onImported }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/members/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cecit-socios-plantilla.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const upload = async (file) => {
    setUploading(true);
    setResult(null);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/members/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (onImported) onImported();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="bg-gradient-to-r from-[#0F2040]/5 to-white border border-gray-200 p-4 mb-6" data-testid="members-import-bar">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-medium text-[#0F2040] mb-1">Importar socios desde Excel</h3>
          <p className="text-xs text-gray-500">
            Descargue la plantilla, rellene los datos y súbala. Los socios existentes (por <em>slug</em> o email)
            se actualizan; los nuevos se crean. Nunca se generan duplicados.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={downloadTemplate}
            data-testid="members-template-download"
            className="inline-flex items-center gap-2 text-sm border border-[#0F2040] text-[#0F2040] px-4 py-2 hover:bg-[#0F2040] hover:text-white transition-colors"
          >
            <Download className="w-4 h-4" /> Descargar plantilla
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            data-testid="members-import-file"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="members-import-upload"
            className="inline-flex items-center gap-2 text-sm bg-[#C8102E] text-white px-4 py-2 hover:bg-[#A30D25] transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Importando…" : "Subir Excel"}
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-4 border-t border-gray-200 pt-3 flex items-center gap-3 flex-wrap text-sm" data-testid="members-import-result">
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
          <span className="text-green-700 font-medium">Importación completada:</span>
          <span className="text-gray-700">
            <b>{result.created}</b> creados · <b>{result.updated}</b> actualizados
            {result.skipped ? <> · <b>{result.skipped}</b> omitidos</> : null}
          </span>
          {result.errors?.length > 0 && (
            <details className="w-full mt-2 text-xs">
              <summary className="cursor-pointer text-amber-700">Ver {result.errors.length} errores</summary>
              <ul className="mt-2 space-y-1 pl-4 list-disc text-gray-600">
                {result.errors.map((er, i) => (
                  <li key={`err-${er.row ?? i}-${i}`}>Fila {er.row}: {er.detail}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 border-t border-red-100 pt-3 flex items-center gap-2 text-sm text-red-700" data-testid="members-import-error">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}
    </div>
  );
}
